// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Meta } from '../types';
import { glossaryTerm } from '../glossary';
import { formatNumber, formatPercent } from '../utils/format';

/** About modal: what this is, where the data comes from, and its limits. */
export function createAbout(meta: Meta): { open: () => void; close: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'About Shark Incidents');

  const n = meta.national;
  modal.innerHTML = `
    <button class="modal-close" aria-label="Close">✕</button>
    <h2>About Shark Incidents</h2>
    <p>
      This is every recorded shark–human ${glossaryTerm('incident', 'incident')} in Australia from
      <strong>${meta.dataFrom}</strong> to <strong>${meta.dataThrough}</strong> — all
      <strong>${formatNumber(n.total)}</strong> of them, from the
      ${glossaryTerm('asid', 'Australian Shark-Incident Database')}. Each is broken down by place, shark species,
      what the person was doing, whether the bite was ${glossaryTerm('provoked', 'provoked')}, and its outcome.
    </p>
    <p>
      It exists to replace fear with pattern. A single "attack" headline hides the shape of the data: about
      <strong>${formatPercent(n.survivePct, 0)}</strong> of people with a known outcome survive, a third of bites are
      provoked, and the ${glossaryTerm('fatality-rate', 'share that prove fatal')} has fallen from roughly a third
      before 1940 to about one in ten today — even as recorded incidents rose. Sharks did not change; rescue, first
      aid and trauma medicine did.
    </p>

    <h3>Where the data comes from</h3>
    <ul>
      <li>
        <strong>Australian Shark-Incident Database (ASID)</strong> — the national record maintained by
        <strong>Taronga Conservation Society Australia</strong> with <strong>Flinders University</strong> and the
        <strong>NSW Department of Primary Industries</strong>, compiled from victim and witness questionnaires,
        fisheries reports and media. It accompanies Riley et&nbsp;al. (2022), <em>Scientific Data</em> 9:378.
      </li>
      <li><strong>ABS ASGS 2021</strong> — state and territory boundaries for the map.</li>
    </ul>

    <h3>How often it updates</h3>
    <p>
      An automated pipeline rebuilds the site once a year, matching ASID's roughly annual public releases. It reads
      the live public version of the database and re-checks it on every build.
    </p>

    <h3>How the numbers are checked</h3>
    <p>
      Two ${glossaryTerm('reconciliation', 'independent fields are cross-checked')} on every build:
      <strong>${formatPercent(meta.reconciliation.boxPct, 1)}</strong> of geolocated incidents fall inside
      Australia's bounding box, and the recorded state matches the coordinates for
      <strong>${formatPercent(meta.reconciliation.agreePct, 1)}</strong> of records. Agreement proves the columns
      were read correctly; the build fails if either drops below tolerance.
    </p>

    <h3>Important limits — please read</h3>
    <ul>
      <li>
        <strong>Rare, not routine.</strong> Across 230+ years this is roughly one recorded incident a month
        nationwide, and most are minor. Drownings, road trips to the beach and countless everyday risks are far more
        dangerous. This is a record of a rare event, not a measure of everyday danger.
      </li>
      <li>
        <strong>Reporting has improved over time.</strong> Older decades under-count minor and non-fatal bites, so
        part of the long-run rise in incidents reflects better record-keeping, not only more encounters.
      </li>
      <li>
        <strong>Counts track people, not shark numbers.</strong> States and beaches with more incidents are mostly
        those with more swimmers and surfers. Per-capita and per-activity views correct for this where possible.
      </li>
      <li>
        <strong>A few fields are missing or estimated.</strong> Species identification, shark length and victim age
        are not always recorded, especially in older or murky-water cases. Missing values are shown as "—", never
        guessed.
      </li>
    </ul>

    <h3>A note on tone</h3>
    <p>
      Sharks are ecologically vital and, to people, statistically almost harmless. This tool is built to inform
      swimmers, surfers and divers and to support shark conservation — not to sensationalise. It is an independent
      project and is not affiliated with Taronga, Flinders University or the NSW DPI.
    </p>
  `;

  document.body.append(overlay, modal);
  const close = () => {
    overlay.classList.remove('open');
    modal.classList.remove('open');
  };
  const open = () => {
    overlay.classList.add('open');
    modal.classList.add('open');
    (modal.querySelector('.modal-close') as HTMLElement)?.focus();
  };
  overlay.addEventListener('click', close);
  modal.querySelector('.modal-close')?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) close();
  });
  return { open, close };
}
